/**
 * UniFi model → fingerprint ID mapping for product icon resolution.
 * Fingerprint icons live at: https://static.ubnt.com/fingerprint/0/{id}_257x257.png
 *
 * This covers the most common UniFi infrastructure devices.
 * Client fingerprints come from the UniFi API response (dev_id field).
 */

// Model string (uppercased) → fingerprint numeric ID
const MODEL_FINGERPRINTS: Record<string, string> = {
  // ─── Gateways / Routers ───
  'UXG': '17236',
  'UXGLI': '16748',
  'UXG-LITE': '16748',
  'UXG-PRO': '16660',
  'UXG-MAX': '17310',
  'UDM': '15048',
  'UDM-PRO': '15340',
  'UDM-PRO-MAX': '17232',
  'UDM-SE': '16820',
  'UDR': '16820',
  'UDB': '17100',
  'USG': '460',
  'USG-PRO-4': '1948',
  'USG-XG-8': '7558',
  'UGW3': '460',
  'UGW4': '1948',

  // ─── Switches ───
  'USW-FLEX': '14898',
  'USW-FLEX-MINI': '14568',
  'USW-LITE-8-POE': '15148',
  'USW-LITE-16-POE': '15150',
  'USW-8-POE': '15148',
  'USW-8': '15148',
  'USW-16-POE': '15150',
  'USW-24': '15152',
  'USW-24-POE': '15152',
  'USW-48': '15154',
  'USW-48-POE': '15154',
  'USW-PRO-24': '15550',
  'USW-PRO-24-POE': '15550',
  'USW-PRO-48': '15552',
  'USW-PRO-48-POE': '15552',
  'USW-PRO-MAX-24-POE': '17130',
  'USW-PRO-MAX-48-POE': '17132',
  'USW-ENTERPRISE-24-POE': '16438',
  'USW-ENTERPRISE-48-POE': '16440',
  'USW-ENTERPRISE-8-POE': '16436',
  'USW-INDUSTRIAL': '15960',
  'USW-MISSION-CRITICAL': '17232',
  'USW-PRO-AGGREGATION': '15554',
  'US-8': '7338',
  'US-8-60W': '7338',
  'US-8-150W': '7340',
  'US-16-150W': '7342',
  'US-24': '7344',
  'US-24-250W': '7344',
  'US-24-500W': '7346',
  'US-48': '7348',
  'US-48-500W': '7348',
  'US-48-750W': '7350',
  'US-XG-6POE': '7558',
  'USL24P': '15152',
  'USL16LP': '15150',
  'USL48P': '15154',

  // ─── Access Points ───
  'U7-PRO': '17050',
  'U7PRO': '17050',
  'U7-LR': '17052',
  'U7-LITE': '17048',
  'U7-PRO-MAX': '17300',
  'U7-OUTDOOR': '17054',
  'U7-PRO-WALL': '17190',
  'U6-PRO': '16060',
  'U6-LR': '16062',
  'U6-LITE': '15998',
  'U6-MESH': '16030',
  'U6-ENTERPRISE': '16360',
  'U6-ENTERPRISE-IW': '16362',
  'U6-IW': '16364',
  'U6-EXTENDER': '16160',
  'U6-PLUS': '16600',
  'UAP-AC-PRO': '3832',
  'UAP-AC-LITE': '3834',
  'UAP-AC-LR': '3836',
  'UAP-AC-MESH': '5060',
  'UAP-AC-MESH-PRO': '5062',
  'UAP-AC-IW': '5158',
  'UAP-AC-HD': '7100',
  'UAP-NANOHD': '9468',
  'UAP-FLEXHD': '13988',
  'UAP-BEACONHD': '14008',
  'UAP-AC-M': '5060',
  'UAP-AC-M-PRO': '5062',
  'UBB': '16988',

  // ─── Cloud Keys / Consoles ───
  'UCK': '5568',
  'UCK-G2': '10288',
  'UCK-G2-PLUS': '10290',
  'UNVR': '15098',
  'UNVR-PRO': '15100',

  // ─── Protect Cameras ───
  'UVC-G4-BULLET': '14528',
  'UVC-G4-PRO': '14530',
  'UVC-G4-DOME': '14532',
  'UVC-G4-PTZ': '15208',
  'UVC-G4-INSTANT': '15458',
  'UVC-G3-FLEX': '9818',
  'UVC-G3-BULLET': '9816',
  'UVC-G3-DOME': '9820',
  'UVC-G3-PRO': '5618',
  'UVC-G5-FLEX': '16988',
  'UVC-G5-PRO': '17100',
  'UVC-AI-PRO': '16968',
  'UVC-AI-360': '16970',
  'UVC-AI-BULLET': '16972',
  'UVC-AI-DSLR': '16974',

  // ─── Doorbells / Access ───
  'UA-G2-PRO': '16500',
  'UACCMPOEAF': '16500',

  // ─── Other ───
  'UDW': '15408',
  'UFP-VIEWPORT': '15458',
  'UP-FLOODLIGHT': '16790',
  'USP-PLUG': '14798',
  'USP-STRIP': '14800',
  'USP-RPS': '15748',
  'UBB-XG': '16988',
}

/**
 * Look up a fingerprint ID from a UniFi model string.
 * Tries exact match first, then normalized match (uppercase, trimmed).
 */
export function getFingerprintForModel(model: unknown): string | null {
  if (!model) return null
  const m = String(model).trim().toUpperCase()

  // Exact match
  if (MODEL_FINGERPRINTS[m]) return MODEL_FINGERPRINTS[m]

  // Try with common variations (remove spaces, hyphens)
  const normalized = m.replace(/[\s-]/g, '')
  for (const [key, val] of Object.entries(MODEL_FINGERPRINTS)) {
    if (key.replace(/[\s-]/g, '') === normalized) return val
  }

  return null
}

/**
 * Get the full icon URL for a fingerprint ID (from static.ubnt.com).
 */
export function getFingerprintIconUrl(fingerprintId: string): string {
  return `https://static.ubnt.com/fingerprint/0/${fingerprintId}_257x257.png`
}

/**
 * Map a device type string to a generic icon category.
 */
export function getDeviceCategory(deviceType: string | null, model: string | null): string {
  const dt = String(deviceType || '').toLowerCase()
  const m = String(model || '').toLowerCase()

  if (dt === 'camera' || m.includes('uvc') || m.includes('camera')) return 'camera'
  if (dt === 'switch' || m.startsWith('usw') || m.startsWith('us-') || m.startsWith('usl')) return 'switch'
  if (dt === 'gateway' || m.startsWith('ugw') || m.startsWith('uxg') || m.startsWith('udm') || m.startsWith('usg') || m.startsWith('udr') || m.startsWith('udb')) return 'gateway'
  if (dt === 'access_point' || m.startsWith('uap') || m.startsWith('u6') || m.startsWith('u7') || m.startsWith('ubb')) return 'ap'
  if (dt === 'nvr' || m.startsWith('unvr')) return 'nvr'
  if (dt === 'server' || m.startsWith('uck')) return 'server'
  if (dt === 'access_controller' || dt === 'controller') return 'access_panel'
  if (dt === 'intercom' || dt === 'doorbell') return 'intercom'
  if (dt === 'fire_panel') return 'fire_panel'
  if (dt === 'voip_phone') return 'phone'
  if (dt === 'ups') return 'ups'
  if (dt === 'workstation') return 'workstation'
  if (dt === 'speaker') return 'speaker'
  if (dt === 'encoder') return 'server'
  if (dt === 'patch_panel') return 'switch'
  return 'unknown'
}
